import { ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { getAllRolesWithPermissions } from "../../../Redux/Users/UsersActions";
import { hasAdminPermission } from "../../../Util/adminPermissions";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { AppDispatch, RootState } from "../../../Redux/store";
import exportToExcel from "../../../Util/exportToExcel";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import styles from "./Roles.module.scss";

interface Role {
  id: number;
  roleName: string;
  permissions: any[];
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Roles() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rolesWithPermissions, rolesWithPermissionsLoading } = useSelector(
    (state: RootState) => state.users,
  );
  const { adminPermissions } = useSelector((state: RootState) => state.auth);
  const canManage = hasAdminPermission(adminPermissions, "roles_manage");

  const [gridApi, setGridApi] = useState<any>(null);
  const [rowData, setRowData] = useState<Role[]>([]);
  const [search, setSearch] = useState<string>("");

  const filteredRowData = rowData?.filter((role) => {
    return role?.roleName?.toLowerCase()?.includes(search?.toLowerCase());
  });

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const columnDefs: ColDef<Role>[] = [
    {
      field: "roleName",
      headerName: "Role Name",
      flex: 1,
    },
    {
      field: "permissions",
      headerName: "Permissions",
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div>
            {params?.data?.permissions
              ?.map((permission: any) => permission?.permissionName)
              .join(", ")}
          </div>
        );
      },
      flex: 4,
    },
    ...(canManage
      ? [
          {
            width: 20,
            colId: "actions",
            sortable: false,
            suppressAutoSize: true,
            suppressMovable: true,
            suppressNavigable: true,
            editable: false,
            cellStyle: {
              pointerEvents: "all",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            },
            cellRenderer: (params: ICellRendererParams) => {
              return (
                <div className={styles.iconContainer}>
                  <Menu
                    direction="left"
                    menuButton={
                      <MenuButton>
                        <HiDotsVertical className={styles.icon} />
                      </MenuButton>
                    }
                    transition
                    portal
                  >
                    <MenuItem
                      onClick={(e) => {
                        navigate(`/admin/roles/edit/${params?.data?.id}`);
                      }}
                    >
                      Edit
                    </MenuItem>
                  </Menu>
                </div>
              );
            },
          } as ColDef<Role>,
        ]
      : []),
  ];

  useEffect(() => {
    if (rolesWithPermissions) {
      setRowData(rolesWithPermissions);
    }
  }, [rolesWithPermissions]);

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAllRolesWithPermissions());
  }, []);

  if (rolesWithPermissionsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingIcon}>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Manage Roles</h1>
          </div>
          <div className={styles.buttonContainer}>
            {filteredRowData?.length > 0 && (
              <button
                className={styles.button}
                onClick={() =>
                  exportToExcel({
                    data: filteredRowData || [],
                    fileName: "Roles",
                  })
                }
              >
                Export
              </button>
            )}
            {canManage && (
              <Link to="/admin/roles/add" className={styles.buttonContainer}>
                <button className={styles.button}>Add Role</button>
              </Link>
            )}
          </div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.doubleInputContainer}>
            <div className={styles.searchContainer}>
              <div className={styles.searchContainerInner}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value || "")}
                />
                <div
                  className={styles.iconContainer}
                  onClick={() => setSearch("")}
                >
                  <IoCloseSharp className={styles.icon} />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default Roles;
